import type { ProgressMark, ProgressStore, StationProgress } from '../../lib/types'

export const PROGRESS_STORAGE_PREFIX = 'ospe-progress:'

interface ParsedProgressEnvelope {
  version: unknown
  stations: unknown
  updatedAt: unknown
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function sanitizeMark(value: unknown): ProgressMark {
  return value === 'known' || value === 'review' || value === 'unseen'
    ? value
    : 'unseen'
}

function sanitizeProgress(raw: unknown): StationProgress | null {
  if (!isObject(raw)) return null

  const mark = sanitizeMark(raw.mark)
  const bookmarked = typeof raw.bookmarked === 'boolean' ? raw.bookmarked : false

  const answeredAt = typeof raw.answeredAt === 'string' && raw.answeredAt.trim().length > 0
    ? raw.answeredAt
    : undefined
  const revealedQuestionIds = Array.isArray(raw.revealedQuestionIds)
    ? Array.from(new Set(raw.revealedQuestionIds
      .filter((value): value is string => typeof value === 'string')
      .map((value) => value.trim())
      .filter(Boolean)))
    : undefined

  return {
    mark,
    bookmarked,
    ...(answeredAt ? { answeredAt } : {}),
    ...(revealedQuestionIds ? { revealedQuestionIds } : {}),
  }
}

export function parseProgressStore(rawJson: string): ProgressStore | null {
  let parsed: unknown

  try {
    parsed = JSON.parse(rawJson)
  } catch {
    return null
  }

  if (!isObject(parsed)) return null

  const envelope = parsed as unknown as ParsedProgressEnvelope

  const stations: Record<string, StationProgress> = {}
  if (isObject(envelope.stations)) {
    for (const [stationId, stationProgressRaw] of Object.entries(envelope.stations)) {
      const normalized = sanitizeProgress(stationProgressRaw)
      if (!normalized) continue
      const trimmedId = stationId.trim()
      if (trimmedId.length === 0) continue
      stations[trimmedId] = normalized
    }
  }

  const updatedAt =
    typeof envelope.updatedAt === 'string' && envelope.updatedAt.trim().length > 0
      ? envelope.updatedAt
      : new Date().toISOString()

  return {
    version: 1,
    stations,
    updatedAt,
  }
}

export function readProgressStore(uid: string, storage?: Storage): ProgressStore | null {
  if (typeof uid !== 'string' || !uid.trim()) return null

  const backend: Storage | null =
    storage
    ?? (typeof window !== 'undefined'
      ? window.localStorage
      : null)

  if (!backend) return null

  try {
    const key = `${PROGRESS_STORAGE_PREFIX}${uid}`
    const storedValue = backend.getItem(key)
    if (!storedValue) return null
    return parseProgressStore(storedValue)
  } catch {
    return null
  }
}

export function readProgressStoreForFallback(uid: string): ProgressStore {
  return {
    version: 1,
    stations: {},
    updatedAt: new Date().toISOString(),
  }
}
